import { useCallback } from 'react';
import { Form } from 'antd';
import { limitDecimal } from '@utils/helpers';
import RangeSliderInput from './RangeSliderInput';

/** Maximum decimal places for display-converted values (bounds and live values). */
const DISPLAY_PRECISION = 1;

/**
 * Smallest step deriveStep may produce. Steps finer than this would let a
 * slider drag commit a value with more decimal places than DISPLAY_PRECISION
 * displays (and than InputNumber's `precision` prop enforces), so every step
 * is floored to this granularity.
 */
const MIN_STEP = Math.pow(10, -DISPLAY_PRECISION);

/** Rounds a numeric value to DISPLAY_PRECISION decimal places without trailing zeros. */
const roundDisplay = v => (v == null ? v : limitDecimal(v, DISPLAY_PRECISION));

/** Floors to DISPLAY_PRECISION (widens min bounds so no data is excluded). */
const floorDisplay = v => {
	if (v == null) return v;
	const factor = Math.pow(10, DISPLAY_PRECISION);
	return Math.floor(v * factor) / factor;
};

/** Ceils to DISPLAY_PRECISION (widens max bounds so no data is excluded). */
const ceilDisplay = v => {
	if (v == null) return v;
	const factor = Math.pow(10, DISPLAY_PRECISION);
	return Math.ceil(v * factor) / factor;
};

/**
 * Form.Item wrapper for a min/max range slider field.
 *
 * Registers a single field in the parent Form with value shape `{ min, max }`.
 * Delegates all input rendering and bound enforcement to RangeSliderInput.
 * Derives step from unit: integer step for percentages, 0.1 for all others.
 *
 * When `conversionFactor` is provided (> 1), the slider/inputs display converted
 * values (e.g. feet instead of meters) while the Form stores metric values.
 * This keeps the filter comparison logic working against raw API data (always
 * metric) while the UI shows the user's preferred measurement system.
 *
 * Usage inside a Form:
 *   <RangeSliderField name="tree_height" min={0} max={30} unit="m" />
 *   <RangeSliderField name="height" min={0} max={30} unit="ft" conversionFactor={3.28} />
 *
 * @param {{
 *   name: string | (string | number)[],
 *   min: number,
 *   max: number,
 *   unit?: string,
 *   conversionFactor?: number,
 *   showSlider?: boolean,
 * }} props
 */

/**
 * Derives a slider step appropriate for the data range.
 *
 * For percentage units where the range spans values >= 1 (e.g. 0–100),
 * an integer step is used. For other ranges, the step is computed as
 * 1/1000th of the range, rounded to the nearest order of magnitude — e.g.
 * a range of 0–100 gets step 0.1. The result is never finer than
 * MIN_STEP, so a range like 0–1 also gets step 0.1 rather than 0.001:
 * every value the slider can reach stays within DISPLAY_PRECISION,
 * matching the precision the inputs display and the Form value stores.
 */
const deriveStep = (min, max, unit) => {
	const range = Math.abs(max - min);
	if (range === 0) return MIN_STEP;
	if (unit === '%' && range >= 1) return 1;
	// 1/1000th of the range, floored to 1 significant digit
	const raw = range / 1000;
	const magnitude = Math.pow(10, Math.floor(Math.log10(raw)));
	return Math.max(magnitude, MIN_STEP);
};

const RangeSliderField = ({ name, min, max, unit, conversionFactor = 1, showSlider = true }) => {
	const hasConversion = conversionFactor !== 1;

	// Round bounds outward to avoid floating-point noise while ensuring no
	// detection is excluded by rounding. Floor for min, ceil for max.
	const displayMin = floorDisplay(hasConversion ? min * conversionFactor : min);
	const displayMax = ceilDisplay(hasConversion ? max * conversionFactor : max);
	const step = deriveStep(displayMin, displayMax, unit);

	// When no conversion is needed, render directly without the adapter layer.
	if (!hasConversion) {
		return (
			<Form.Item name={name} noStyle>
				<RangeSliderInput
					min={displayMin}
					max={displayMax}
					step={step}
					precision={DISPLAY_PRECISION}
					showSlider={showSlider}
				/>
			</Form.Item>
		);
	}

	// With conversion: intercept the Form ↔ Input contract to convert at the boundary.
	// Form stores metric; RangeSliderInput sees and emits display-unit values.
	return (
		<Form.Item name={name} noStyle>
			<ConvertedRangeSlider
				min={displayMin}
				max={displayMax}
				step={step}
				precision={DISPLAY_PRECISION}
				showSlider={showSlider}
				conversionFactor={conversionFactor}
			/>
		</Form.Item>
	);
};

/**
 * Adapter that converts between metric (Form value) and display units
 * (RangeSliderInput value). Sits between Form.Item and RangeSliderInput.
 *
 * Null-safe: individual bounds may be null (user cleared the field); the
 * arithmetic is only applied to numeric values so null passes through
 * unchanged in both directions.
 */
const ConvertedRangeSlider = ({ value, onChange, conversionFactor, ...rest }) => {
	// Metric → display (null-safe)
	const displayValue = value
		? {
				min: value.min != null ? roundDisplay(value.min * conversionFactor) : null,
				max: value.max != null ? roundDisplay(value.max * conversionFactor) : null,
			}
		: undefined;

	// Display → metric (null-safe)
	const handleChange = useCallback(
		displayVal => {
			if (!displayVal) return onChange?.(displayVal);
			onChange?.({
				min: displayVal.min != null ? displayVal.min / conversionFactor : null,
				max: displayVal.max != null ? displayVal.max / conversionFactor : null,
			});
		},
		[onChange, conversionFactor]
	);

	return <RangeSliderInput value={displayValue} onChange={handleChange} {...rest} />;
};

export default RangeSliderField;
